import type { CaptureFileJSONObject, DistanceBearingPitch } from '@ikegps/core-api';
import { CollectionModel, IkeUnit } from '@ikegps/core-shared';
import { addStyles, DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { CaptureThumbnail } from '../capture.thumbnail/capture.thumbnail';
import { CSS as CaptureThumbnailCSS } from '../capture.thumbnail/capture.thumbnail.style';

const CSS = {
	container: 'c-FieldInfo',
	captures: 'c-FieldInfo__Captures',
	stat: {
		container: 'c-FieldInfo__Stat',
		label: 'c-FieldInfo__Stat--label',
		name: 'c-FieldInfo__Stat--name',
		icon: 'c-FieldInfo__Stat--icon',
		value: 'c-FieldInfo__Stat--value',
	},
};

addStyles({
	[CSS.container]: {
		width: '100%',
	},
	[CSS.stat.container]: {
		width: '100%',
		display: 'flex',
		'justify-content': 'space-between',
		'margin-left': DefaultTheme.space(2),
	},
	[CSS.stat.label]: {
		flex: 1,
		display: 'flex',
	},
	[CSS.stat.icon]: {
		'font-size': DefaultTheme.font.size,
		color: DefaultTheme.background.mediumGrey,
		'margin-left': DefaultTheme.space(1),
	},
	[CSS.stat.value]: {
		flex: 1,
		color: DefaultTheme.font.color.supporting,
	},
	[CSS.captures]: {
		width: '100%',
		display: 'flex',
		'justify-content': 'space-around',
		'margin-top': DefaultTheme.space(1),
		'overflow-x': 'auto',
	},
	[`${CSS.captures} .${CaptureThumbnailCSS.container}`]: {
		width: '130px',
		'min-width': '130px',
		height: '130px',
		'min-height': '130px',
	},
});

export interface FieldStat {
	name: T;
	/** Value in meters or display value */
	value: number | string;
}

const THUMBNAIL_CTX = {
	crosshair: true,
	title: '',
};

export const FieldInfo = {
	viewCaptures(collection: CollectionModel, captures: CaptureFileJSONObject[], stats: FieldStat[] = []): MithrilVNode {
		const captureChildren: MithrilVNode[] = [];
		for (const capture of captures) {
			THUMBNAIL_CTX.title = Services.DateTime.formatDateTime(capture.collectedAt.iso);
			captureChildren.push(CaptureThumbnail.view(collection, capture, THUMBNAIL_CTX));
		}

		const statNodes: MithrilVNode[] = [];
		for (const stat of stats) {
			statNodes.push(FieldInfo.viewStat(stat.name, stat.value));
		}

		return m('div', { className: CSS.container }, [
			...statNodes,
			m('div', { className: CSS.captures }, captureChildren),
		]);
	},

	viewStat(name: T, value: number | string): MithrilVNode {
		let displayValue: string;
		if (typeof value === 'number') {
			displayValue = IkeUnit.display(value, Services.UserSettings.units);
		} else {
			displayValue = value;
		}

		return m('div', { className: CSS.stat.container }, [
			m('div', { className: CSS.stat.label }, Translate(name)),
			m('span', { className: CSS.stat.value }, displayValue),
		]);
	},

	viewVectorInline(value: DistanceBearingPitch): string {
		const length = IkeUnit.display(value.distance, Services.UserSettings.units, IkeUnit.METER);
		const angle = IkeUnit.display(value.bearing, IkeUnit.DEGREE, IkeUnit.RADIAN);
		return Translate(T.FIELD_VECTOR_P2P_DISPLAY, { length, angle });
	},
};
